import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiExternalLink, FiUser, FiAward, FiClock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import DOMPurify from 'dompurify';
import deploymentConfig from '../config/deployment';
import { useTranslation } from '../hooks/useTranslation';

const ViewUserNFT = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = searchParams.get('user') || searchParams.get('id') || searchParams.get('username');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!userId) {
      setError('No user specified. Please provide a user ID or username.');
      setLoading(false);
      return;
    }

    fetchUserNFTs();
  }, [userId]);

  const fetchUserNFTs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(deploymentConfig.getApiUrl(`api/users/${userId}/nfts`));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('User not found');
        }
        throw new Error('Failed to fetch user NFTs');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching user NFTs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('data:')) return url;
    // Remove /api suffix from apiBaseUrl for static files
    const baseUrl = (deploymentConfig.apiBaseUrl || 'https://api.exploreandwin.com').replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: 'bg-gray-100 text-gray-800 border-gray-300',
      uncommon: 'bg-green-100 text-green-800 border-green-300',
      rare: 'bg-blue-100 text-blue-800 border-blue-300',
      epic: 'bg-primary-100 text-primary-800 border-primary-300',
      legendary: 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    return colors[rarity?.toLowerCase()] || colors.common;
  };

  const getStatusColor = (status) => {
    const colors = {
      minted: 'bg-green-100 text-green-800',
      transferred: 'bg-primary-100 text-primary-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const renderNFTImage = (nft) => {
    const imageUrl = getFullImageUrl(nft.image);

    if (!imageUrl) {
      // Fallback: Generate gradient with emoji
      const emoji = nft.template?.emoji || '🏆';
      return (
        <div className="w-full h-48 bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center text-6xl">
          {emoji}
        </div>
      );
    }

    if (imageUrl.startsWith('data:image/svg+xml')) {
      // SVG data URL
      return (
        <div
          className="w-full h-48"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize((() => {
              try {
                return decodeURIComponent(imageUrl.replace('data:image/svg+xml,', ''));
              } catch (e) {
                return imageUrl.replace('data:image/svg+xml,', '');
              }
            })(), { USE_PROFILES: { svg: true } })
          }}
        />
      );
    }

    // Regular image URL
    return (
      <img
        src={imageUrl}
        alt={nft.name}
        className="w-full h-48 object-cover"
        onError={(e) => {
          // Fallback to gradient with emoji
          const emoji = nft.template?.emoji || '🎨';
          e.target.parentElement.innerHTML = `
            <div class="w-full h-48 bg-gradient-to-br from-primary-400 to-blue-400 flex items-center justify-center text-6xl">
              ${emoji}
            </div>
          `;
        }}
      />
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('cms.common.loading')}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <FiAlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/users')}
            className="btn-primary"
          >
            {t('cms.common.back')}
          </button>
        </div>
      </div>
    );
  }

  const { user, nfts, totalNFTs } = data || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-3xl">
              {user?.avatarUrl ? (
                <img
                  src={getFullImageUrl(user.avatarUrl)}
                  alt={user.displayName}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <FiUser className="w-10 h-10" />
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold">{user?.displayName}'s NFT Collection</h1>
              <p className="text-white/80 mt-1">@{user?.username}</p>
              {user?.walletAddress && (
                <p className="text-white/70 text-sm mt-2 font-mono">
                  {user.walletAddress.slice(0, 12)}...{user.walletAddress.slice(-8)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600">{totalNFTs}</div>
              <div className="text-sm text-gray-600 mt-1">Total NFTs</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-secondary-600">
                {nfts?.filter(n => n.status === 'minted' || n.status === 'transferred').length || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Minted</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-600">
                {nfts?.filter(n => n.achievement).length || 0}
              </div>
              <div className="text-sm text-gray-600 mt-1">Achievement NFTs</div>
            </div>
          </div>
        </div>
      </div>

      {/* NFT Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {nfts && nfts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {nfts.map((nft) => (
              <div key={nft.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                {/* NFT Image */}
                <div className="relative">
                  {renderNFTImage(nft)}

                  {/* Rarity Badge */}
                  {nft.template?.rarity && (
                    <div className="absolute top-2 right-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold border ${getRarityColor(nft.template.rarity)}`}>
                        {nft.template.rarity.toUpperCase()}
                      </span>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(nft.status)}`}>
                      {nft.status === 'minted' && <FiCheckCircle className="inline w-3 h-3 mr-1" />}
                      {nft.status}
                    </span>
                  </div>
                </div>

                {/* NFT Details */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{nft.name}</h3>

                  {nft.description && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{nft.description}</p>
                  )}

                  {/* Template Info */}
                  {nft.template?.genre && (
                    <div className="flex items-center space-x-2 mt-2">
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                        {nft.template.genre}
                      </span>
                      {nft.template.emoji && (
                        <span className="text-lg">{nft.template.emoji}</span>
                      )}
                    </div>
                  )}

                  {/* Achievement Info */}
                  {nft.achievement && Object.keys(nft.achievement).length > 0 && (
                    <div className="mt-3 p-2 bg-primary-50 rounded border-l-4 border-primary-400">
                      <div className="flex items-center text-primary-700 text-sm">
                        <FiAward className="w-4 h-4 mr-2" />
                        <span className="font-medium">
                          {nft.achievement.title || 'Achievement NFT'}
                        </span>
                      </div>
                      {nft.achievement.points && (
                        <div className="text-xs text-primary-600 mt-1">
                          {nft.achievement.points} points
                        </div>
                      )}
                    </div>
                  )}

                  {/* Attributes */}
                  {nft.attributes && nft.attributes.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {nft.attributes.slice(0, 3).map((attr, idx) => (
                        <div key={idx} className="text-xs flex justify-between">
                          <span className="text-gray-500">{attr.trait_type}:</span>
                          <span className="text-gray-900 font-medium">{attr.value}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Blockchain Info */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center">
                        <FiClock className="w-3 h-3 mr-1" />
                        {new Date(nft.createdAt).toLocaleDateString()}
                      </span>
                      <span className="font-mono">
                        {nft.blockchain} • {nft.network}
                      </span>
                    </div>

                    {/* Asset ID */}
                    {nft.assetId && (
                      <div className="mt-2 text-xs">
                        <span className="text-gray-500">Asset ID:</span>
                        <span className="ml-2 font-mono text-gray-900">{nft.assetId}</span>
                      </div>
                    )}
                  </div>

                  {/* Links */}
                  <div className="mt-3 flex space-x-2">
                    {nft.explorerUrl && (
                      <a
                        href={nft.explorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-center px-3 py-2 bg-primary-100 text-primary-700 rounded hover:bg-primary-200 text-xs font-medium inline-flex items-center justify-center"
                      >
                        <FiExternalLink className="w-3 h-3 mr-1" />
                        Transaction
                      </a>
                    )}
                    {nft.assetUrl && (
                      <a
                        href={nft.assetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-center px-3 py-2 bg-secondary-100 text-secondary-700 rounded hover:bg-secondary-200 text-xs font-medium inline-flex items-center justify-center"
                      >
                        <FiExternalLink className="w-3 h-3 mr-1" />
                        Asset
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FiAward className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900">No NFTs Yet</h3>
            <p className="text-gray-500 mt-2">
              {user?.displayName} hasn't minted any NFTs yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewUserNFT;
